import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Power, AlertTriangle } from 'lucide-react';

const NCFManagement = ({ user }) => {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    tipo_ncf: 'B02',
    secuencia_desde: '',
    secuencia_hasta: '',
    fecha_autorizacion: '',
    fecha_vencimiento: '',
  });

  useEffect(() => {
    fetchRanges();
  }, []);

  const fetchRanges = async () => {
    try {
      const response = await axios.get(`${API}/ncf-ranges`);
      setRanges(response.data);
    } catch (error) {
      toast.error('Error al cargar rangos de NCF');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRange = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/ncf-ranges`, {
        ...formData,
        secuencia_desde: parseInt(formData.secuencia_desde),
        secuencia_hasta: parseInt(formData.secuencia_hasta),
        fecha_autorizacion: new Date(formData.fecha_autorizacion).toISOString(),
        fecha_vencimiento: new Date(formData.fecha_vencimiento).toISOString(),
      });
      toast.success('Rango de NCF creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchRanges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear rango');
    }
  };

  const handleToggleRange = async (rangeId) => {
    try {
      await axios.patch(`${API}/ncf-ranges/${rangeId}/toggle`);
      toast.success('Estado actualizado');
      fetchRanges();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteRange = async (rangeId) => {
    if (window.confirm('¿Está seguro de eliminar este rango?')) {
      try {
        await axios.delete(`${API}/ncf-ranges/${rangeId}`);
        toast.success('Rango eliminado');
        fetchRanges();
      } catch (error) {
        toast.error('Error al eliminar rango');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_ncf: 'B02',
      secuencia_desde: '',
      secuencia_hasta: '',
      fecha_autorizacion: '',
      fecha_vencimiento: '',
    });
  };

  const getTipoNCFLabel = (tipo) => {
    const labels = {
      'B01': 'B01 - Crédito Fiscal',
      'B02': 'B02 - Consumidor Final',
      'B03': 'B03 - Nota de Débito',
      'B04': 'B04 - Nota de Crédito',
    };
    return labels[tipo] || tipo;
  };

  const getStatusColor = (disponibles, vencido) => {
    if (vencido) return 'bg-red-100 text-red-700';
    if (disponibles < 50) return 'bg-red-100 text-red-700';
    if (disponibles < 100) return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="ncf-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800">
            Gestión de Comprobantes Fiscales
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            Administra los rangos de NCF autorizados por la DGII
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto" data-testid="create-range-btn">
              <Plus className="mr-2" size={18} />
              <span className="text-sm md:text-base">Nuevo Rango</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="create-range-dialog">
            <DialogHeader>
              <DialogTitle>Crear Rango de NCF</DialogTitle>
              <DialogDescription>Ingrese los datos del rango autorizado por DGII</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRange} className="space-y-4">
              <div>
                <Label htmlFor="tipo_ncf">Tipo de NCF *</Label>
                <Select value={formData.tipo_ncf} onValueChange={(value) => setFormData({ ...formData, tipo_ncf: value })} required>
                  <SelectTrigger data-testid="form-tipo-ncf-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B01">B01 - Crédito Fiscal</SelectItem>
                    <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                    <SelectItem value="B03">B03 - Nota de Débito</SelectItem>
                    <SelectItem value="B04">B04 - Nota de Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="secuencia_desde">Desde *</Label>
                  <Input
                    id="secuencia_desde"
                    type="number"
                    value={formData.secuencia_desde}
                    onChange={(e) => setFormData({ ...formData, secuencia_desde: e.target.value })}
                    required
                    placeholder="00000001"
                    data-testid="form-desde-input"
                  />
                </div>
                <div>
                  <Label htmlFor="secuencia_hasta">Hasta *</Label>
                  <Input
                    id="secuencia_hasta"
                    type="number"
                    value={formData.secuencia_hasta}
                    onChange={(e) => setFormData({ ...formData, secuencia_hasta: e.target.value })}
                    required
                    placeholder="00001000"
                    data-testid="form-hasta-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fecha_autorizacion">Fecha Autorización *</Label>
                <Input
                  id="fecha_autorizacion"
                  type="date"
                  value={formData.fecha_autorizacion}
                  onChange={(e) => setFormData({ ...formData, fecha_autorizacion: e.target.value })}
                  required
                  data-testid="form-fecha-aut-input"
                />
              </div>
              <div>
                <Label htmlFor="fecha_vencimiento">Fecha Vencimiento *</Label>
                <Input
                  id="fecha_vencimiento"
                  type="date"
                  value={formData.fecha_vencimiento}
                  onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                  required
                  data-testid="form-fecha-venc-input"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-range-btn">
                Crear Rango
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alert for low NCF */}
      {ranges.some((r) => r.is_active && r.disponibles < 100) && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="text-amber-500 mr-3" size={24} />
            <div>
              <h3 className="font-semibold text-amber-800">Advertencia de NCF</h3>
              <p className="text-sm text-amber-700">
                Uno o más rangos de NCF tienen menos de 100 comprobantes disponibles
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ranges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {ranges.map((range) => {
          const vencido = new Date(range.fecha_vencimiento) < new Date();
          return (
            <Card key={range.id} className={`${!range.is_active && 'opacity-60'}`} data-testid={`range-card-${range.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{getTipoNCFLabel(range.tipo_ncf)}</CardTitle>
                    <CardDescription>
                      Creado: {new Date(range.created_at).toLocaleDateString('es-DO')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleRange(range.id)}
                      data-testid={`toggle-range-${range.id}`}
                    >
                      <Power size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteRange(range.id)}
                      data-testid={`delete-range-${range.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Desde:</span>
                      <div className="font-mono font-semibold">{range.tipo_ncf}{range.secuencia_desde.toString().padStart(8, '0')}</div>
                    </div>
                    <div>
                      <span className="text-slate-600">Hasta:</span>
                      <div className="font-mono font-semibold">{range.tipo_ncf}{range.secuencia_hasta.toString().padStart(8, '0')}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Usados:</span>
                      <div className="font-semibold">{range.usados.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-slate-600">Disponibles:</span>
                      <div className={`font-semibold ${range.disponibles < 100 ? 'text-red-600' : 'text-green-600'}`}>
                        {range.disponibles.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Autorización:</span>
                      <span>{new Date(range.fecha_autorizacion).toLocaleDateString('es-DO')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Vencimiento:</span>
                      <span className={vencido ? 'text-red-600 font-semibold' : ''}>
                        {new Date(range.fecha_vencimiento).toLocaleDateString('es-DO')}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <span
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                        range.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {range.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                    {vencido && (
                      <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                        Vencido
                      </span>
                    )}
                    {range.disponibles < 50 && range.is_active && !vencido && (
                      <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                        Crítico
                      </span>
                    )}
                    {range.disponibles < 100 && range.disponibles >= 50 && range.is_active && !vencido && (
                      <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                        Alerta
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {ranges.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto text-slate-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No hay rangos de NCF configurados</h3>
          <p className="text-slate-600 mb-4">Agregue los rangos autorizados por la DGII para comenzar</p>
        </div>
      )}
    </div>
  );
};

export default NCFManagement;
